/**
 * Centralized error handling for Subiza.
 *
 * Responsibilities:
 *   1. Normalize arbitrary thrown values into `AppError` instances with a stable
 *      code from `ERROR_CATALOG`.
 *   2. Attach a friendly message + actionable hint for the affected user.
 *   3. Optionally attach raw technical cause for verified superadmins ONLY.
 *
 * ---------------------------------------------------------------------------
 * WHY THE DETAIL HANDLING LOOKS INDIRECT (read before editing)
 * ---------------------------------------------------------------------------
 * TanStack Start does not hand-throw server-function errors to the browser. It
 * runs them through Seroval (`toCrossJSONAsync`) in
 * `@tanstack/start-server-core/dist/esm/server-functions-handler.js`, and
 * Seroval's Error support copies EVERY own property of the error object
 * (`Object.getOwnPropertyNames`) onto the reconstructed error on the client.
 *
 * Two consequences drive the whole design of this file:
 *
 *   A) `error instanceof AppError` is FALSE in the browser. Seroval rebuilds a
 *      plain `Error` and copies the AppError fields onto it, so the prototype
 *      is lost. Detection must therefore be structural, not `instanceof`.
 *
 *   B) Anything assigned to `appError.detail` is shipped to EVERY caller,
 *      including anonymous students on `/play/$pin`, and is visible in devtools
 *      regardless of what the UI decides to render. Gating the *rendering* of a
 *      field is not gating the field itself.
 *
 * So raw technical text is never stored on the error object by default. It is
 * staged in a module-level `WeakMap`, which is invisible to Seroval because it
 * is not an own property. `toClientError()` promotes the staged text onto
 * `detail` only after a server-side `has_role` check confirms the caller is a
 * superadmin. Everyone else gets an empty `detail` and the raw text stays in
 * the server log.
 */

/**
 * Catalog of stable error codes and their user-facing presentation.
 *
 * `title` is what the UI shows as the headline. `userMessage` is the toast /
 * inline body. `hint` is the actionable next step. `retryable` drives whether
 * the UI offers a "Try again" affordance.
 */
const ERROR_CATALOG = Object.freeze({
  // --- Authentication ------------------------------------------------------
  AUTH_INVALID_CREDENTIALS: {
    title: "Sign-in failed",
    userMessage: "That email and password don't match.",
    hint: "Check for typos, or reset your password.",
    retryable: false,
  },
  AUTH_EMAIL_UNCONFIRMED: {
    title: "Confirm your email",
    userMessage: "Confirm your email address first.",
    hint: "Open the link we sent you, then sign in again.",
    retryable: false,
  },
  AUTH_SESSION_EXPIRED: {
    title: "Session expired",
    userMessage: "Your session expired.",
    hint: "Sign in again to continue.",
    retryable: false,
  },
  AUTH_ACCOUNT_DISABLED: {
    title: "Account disabled",
    userMessage: "This account has been disabled.",
    hint: "Contact your administrator.",
    retryable: false,
  },
  AUTH_RATE_LIMITED: {
    title: "Too many attempts",
    userMessage: "Too many attempts. Please wait a moment and try again.",
    hint: "If this keeps happening, reset your password or contact support.",
    retryable: true,
  },

  // --- Email OTP -----------------------------------------------------------
  // Codes used to be passed as `catalogError("INVALID_CODE")` and
  // `catalogError("RATE_LIMITED")`, neither of which is a catalog key. Both
  // silently degraded to UNKNOWN, so a mistyped code and a genuinely broken
  // backend produced the identical toast and the identical Sentry event.
  AUTH_INVALID_CODE: {
    title: "Incorrect code",
    userMessage: "That code isn't right.",
    hint: "Check the digits and try again, or request a new code.",
    retryable: true,
  },
  AUTH_CODE_EXPIRED: {
    title: "Code expired",
    userMessage: "That code has expired.",
    hint: "Request a new code and enter it again.",
    retryable: true,
  },
  AUTH_TOO_MANY_ATTEMPTS: {
    title: "Too many attempts",
    userMessage: "Too many failed attempts.",
    hint: "Request a new code to continue.",
    retryable: true,
  },
  EMAIL_NOT_CONFIGURED: {
    title: "Email unavailable",
    userMessage: "We couldn't send the verification email.",
    hint: "Try again shortly, or ask an administrator to check the mail settings.",
    retryable: true,
  },

  // --- Authorization -------------------------------------------------------
  FORBIDDEN: {
    title: "Not allowed",
    userMessage: "You don't have permission to do that.",
    hint: "Ask a superadmin to grant you access.",
    retryable: false,
  },

  // --- Resources and constraints ------------------------------------------
  NOT_FOUND: {
    title: "Not found",
    userMessage: "We couldn't find what you were looking for.",
    hint: "It may have been deleted. Go back and try again.",
    retryable: false,
  },
  DUPLICATE: {
    title: "Already exists",
    userMessage: "That already exists.",
    hint: "Rename it or open the existing one instead.",
    retryable: false,
  },

  // --- Validation ----------------------------------------------------------
  INVALID_INPUT: {
    title: "Check your input",
    userMessage: "Some of the details you entered aren't valid.",
    hint: "Fix the highlighted fields and try again.",
    retryable: false,
  },

  // --- AI service ----------------------------------------------------------
  AI_RATE_LIMITED: {
    title: "AI is busy",
    userMessage: "The AI is busy right now.",
    hint: "Wait about a minute, then try again.",
    retryable: true,
  },
  AI_UNAVAILABLE: {
    title: "AI unavailable",
    userMessage: "AI quiz generation is temporarily unavailable.",
    hint: "Try again in a few minutes.",
    retryable: true,
  },
  AI_NOT_CONFIGURED: {
    title: "AI not configured",
    userMessage: "AI generation isn't set up on this server.",
    hint: "Ask a superadmin to add the API key.",
    retryable: false,
  },
  AI_NOTES_TOO_LARGE: {
    title: "Notes too long",
    userMessage: "Those notes are too long for the AI to process.",
    hint: "Upload a shorter document.",
    retryable: false,
  },
  AI_MODEL_GONE: {
    title: "AI model unavailable",
    userMessage: "The configured AI model is no longer available.",
    hint: "Ask a superadmin to update the model.",
    retryable: false,
  },
  AI_EMPTY_RESPONSE: {
    title: "No AI response",
    userMessage: "The AI returned nothing usable.",
    hint: "Try rephrasing the topic, or generate again.",
    retryable: true,
  },
  AI_BLOCKED: {
    title: "AI declined",
    userMessage: "The AI declined to answer that request.",
    hint: "Try rephrasing the topic or removing the reference material.",
    retryable: false,
  },
  // --- AI quota gateway (docs/subiza-quota-gateway-spec.md §10) --------------
  // Stable machine codes surfaced by the ai-gateway choke point. Each maps to a
  // fixed HTTP status in src/lib/ai-gateway/gateway-errors.js.
  USER_DAILY_LIMIT: {
    title: "Daily exam limit reached",
    userMessage: "You've reached the limit of exams you can generate today.",
    hint: "The limit resets at midnight (Pacific time). Check the quota page tomorrow.",
    retryable: false,
  },
  AI_BUSY: {
    title: "AI is at capacity",
    userMessage: "The AI has used up today's free capacity.",
    hint: "It resets at midnight (Pacific time) — try again tomorrow.",
    retryable: false,
  },
  RATE_LIMITED: {
    title: "Please wait a moment",
    userMessage: "The AI is processing a lot of requests right now.",
    hint: "Wait a few seconds and try again.",
    retryable: true,
  },
  VALIDATION_ERROR: {
    title: "Check your input",
    userMessage: "That request doesn't meet our limits.",
    hint: "Shorten the topic or notes, or reduce the number of questions.",
    retryable: false,
  },
  UPSTREAM_ERROR: {
    title: "AI service error",
    userMessage: "The AI service couldn't be reached.",
    hint: "Try again in a few minutes.",
    retryable: true,
  },
  MARKING_PENDING: {
    title: "Marking pending",
    userMessage: "Your answers are saved and will be marked automatically.",
    hint: "The result will appear here once marking is done.",
    retryable: false,
  },

  // --- Game / PIN ----------------------------------------------------------
  INVALID_PIN: {
    title: "Wrong game code",
    userMessage: "That game code isn't right.",
    hint: "Check the code your teacher gave you.",
    retryable: false,
  },
  GAME_ENDED: {
    title: "Game finished",
    userMessage: "This game has ended.",
    hint: "Ask your teacher for a new game.",
    retryable: false,
  },

  // --- Connectivity --------------------------------------------------------
  NETWORK_UNAVAILABLE: {
    title: "You're offline",
    userMessage: "You appear to be offline.",
    hint: "Reconnect and try again.",
    retryable: true,
  },

  // --- Fallback ------------------------------------------------------------
  UNKNOWN: {
    title: "Something went wrong",
    userMessage: "Something went wrong on our end.",
    hint: "Try again. If it keeps happening, please contact support.",
    retryable: true,
  },
});

/**
 * Raw technical text, keyed by the AppError it belongs to.
 *
 * A WeakMap (rather than a property on the error) is the whole point: Seroval
 * only copies *own properties* of a thrown error, so anything kept here is
 * structurally incapable of reaching the browser. Entries are also collected
 * automatically, so a long-running Worker cannot leak memory.
 */
const RAW_DETAIL = new WeakMap();

/**
 * An error with a stable code and a presentation-ready message.
 *
 * `detail` is intentionally empty unless a verified superadmin caused it to be
 * populated. See the file header for why.
 */
class AppError extends Error {
  /**
   * @param {string} code Stable code, ideally a key of `ERROR_CATALOG`.
   * @param {string} userMessage Friendly message shown to the affected user.
   * @param {string} hint Actionable next step.
   * @param {boolean} retryable Whether retrying could plausibly succeed.
   * @param {string} area Operation identifier, e.g. "quiz.save".
   * @param {string} [detail] Verified-superadmin-only technical cause.
   */
  constructor(code, userMessage, hint, retryable = false, area = "", detail = "") {
    // Note: the friendly message becomes `error.message`, which is also what
    // Seroval ships. That is intentional: `message` must never carry raw
    // database text, only the friendly copy.
    super(userMessage);
    this.name = "AppError";
    this.code = code;
    this.userMessage = userMessage;
    this.hint = hint;
    this.retryable = retryable;
    this.area = area;
    this.detail = detail;
    /** Wall-clock time the error was created, so panels show when it happened. */
    this.occurredAt = Date.now();
  }
}

/**
 * Structural AppError check that survives the Seroval boundary.
 *
 * @param {unknown} value Candidate error.
 * @returns {boolean} True when the value carries a known catalog code.
 */
function isAppErrorLike(value) {
  if (value instanceof AppError) return true;
  return Boolean(
    value &&
    typeof value === "object" &&
    typeof value.code === "string" &&
    Object.hasOwn(ERROR_CATALOG, value.code) &&
    typeof value.userMessage === "string",
  );
}

/**
 * Look up a catalog entry, falling back to `UNKNOWN`.
 *
 * @param {string} code Candidate error code.
 * @returns {{title: string, userMessage: string, hint: string, retryable: boolean}} Catalog entry.
 */
function catalogEntry(code) {
  return Object.hasOwn(ERROR_CATALOG, code) ? ERROR_CATALOG[code] : ERROR_CATALOG.UNKNOWN;
}

/**
 * Stage raw technical text for an AppError without making it serializable.
 *
 * @param {AppError} appError Normalized error.
 * @param {string} raw Raw technical text (DB message, provider response, ...).
 * @returns {AppError} The same instance, for chaining.
 */
function stageRawDetail(appError, raw) {
  if (typeof raw === "string" && raw.length > 0) {
    RAW_DETAIL.set(appError, raw);
  }
  return appError;
}

/**
 * Read the raw technical text staged for an AppError.
 *
 * @param {AppError} appError Normalized error.
 * @returns {string} The staged text, or "" when nothing was staged.
 */
function rawDetailFor(appError) {
  if (typeof appError?.detail === "string" && appError.detail.length > 0) {
    return appError.detail;
  }
  return RAW_DETAIL.get(appError) ?? "";
}

/**
 * Build an AppError from a catalog code, staging the raw cause privately.
 *
 * @param {string} code Catalog key.
 * @param {object} [options]
 * @param {string} [options.area] Operation identifier.
 * @param {string} [options.raw] Raw technical cause (kept off the error object).
 * @param {string} [options.message] Override for the friendly message.
 * @returns {AppError} Normalized error.
 */
function fromCatalog(code, { area = "", raw = "", message } = {}) {
  const entry = catalogEntry(code);
  const appError = new AppError(
    code,
    message || entry.userMessage,
    entry.hint,
    entry.retryable,
    area,
    "",
  );
  return stageRawDetail(appError, raw);
}

/**
 * Extract the most useful raw technical text from a thrown value.
 *
 * Supabase/Postgres errors carry the interesting bits in `detail` and `hint`
 * rather than `message`, and the AI gateway's upstream caller parks its
 * specifics in the standard `cause` field. All of them are considered, in the
 * order a superadmin is most likely to want them.
 *
 * @param {any} error Raw error.
 * @returns {string} Raw technical text, or "" when there is nothing useful.
 */
function rawTextOf(error) {
  if (!error || typeof error !== "object") return typeof error === "string" ? error : "";

  // `cause` may be an Error, a string, or something else entirely.
  const cause =
    typeof error.cause === "string"
      ? error.cause
      : error.cause && typeof error.cause.message === "string"
        ? error.cause.message
        : "";

  const parts = [error.detail, error.message, cause, error.hint].filter(
    (part) => typeof part === "string" && part.length > 0,
  );
  if (parts.length === 0) return String(error);
  // Postgres splits the same information across `message` and `detail`; keep
  // both but avoid repeating identical strings.
  return Array.from(new Set(parts)).join(" | ");
}

/**
 * Supabase Auth codes that mean "this caller is being throttled" rather than
 * "this request was wrong".
 *
 * This set is the single source of truth for the mapping below and for the
 * sign-up fallback in `auth.jsx`, which has to recognise the same codes
 * without routing a live error through `toAppError` first.
 */
const AUTH_RATE_LIMIT_CODES = new Set([
  "over_request_rate_limit",
  "over_email_send_rate_limit",
  "too_many_requests",
]);

/**
 * Whether a Supabase Auth error code reports throttling.
 *
 * @param {string} code Supabase Auth error code, e.g. "over_request_rate_limit".
 * @returns {boolean} True when the request was rate limited.
 */
function isRateLimitCode(code) {
  return AUTH_RATE_LIMIT_CODES.has(code);
}

/**
 * Map a Supabase Auth error code to a catalog entry.
 *
 * Auth failures arrive directly from the browser (there is no server function
 * in the way), and they are the errors most likely to be seen by a nervous
 * first-time user: "Invalid login credentials" reads as an accusation, and a
 * raw "User already registered" leaks that an address is in use. These codes
 * must be resolved BEFORE the Postgres mapping, which would otherwise catch
 * them in its `default` branch and report every wrong password as a server
 * fault.
 *
 * @param {string} code Supabase Auth error code, e.g. "invalid_credentials".
 * @returns {string} Catalog key.
 */
function catalogKeyForAuthCode(code) {
  if (isRateLimitCode(code)) return "AUTH_RATE_LIMITED";

  switch (code) {
    case "invalid_credentials":
    case "provided_invalid_credentials":
      return "AUTH_INVALID_CREDENTIALS";
    case "email_not_confirmed":
      return "AUTH_EMAIL_UNCONFIRMED";
    case "email_exists":
    case "user_already_exists":
      return "DUPLICATE";
    case "weak_password":
    case "password_too_short":
    case "validation_failed":
      return "INVALID_INPUT";
    case "session_not_found":
    case "refresh_token_not_found":
    case "refresh_token_already_used":
      return "AUTH_SESSION_EXPIRED";
    default:
      return "UNKNOWN";
  }
}

/**
 * Map a Postgres error code to a catalog entry.
 *
 * @param {string} code Postgres error code, e.g. "23505".
 * @returns {string} Catalog key.
 */
function catalogKeyForPostgresCode(code) {
  switch (code) {
    case "23505": // unique_violation
      return "DUPLICATE";
    case "23503": // foreign_key_violation
    case "23502": // not_null_violation
    case "22P02": // invalid_text_representation
      return "INVALID_INPUT";
    case "42501": // insufficient_privilege (RLS denial)
      return "FORBIDDEN";
    case "PGRST116": // zero rows returned by a single() lookup
      return "NOT_FOUND";
    case "PGRST301": // expired JWT
    case "PGRST302": // JWT audience / issuer mismatch
      return "AUTH_SESSION_EXPIRED";
    default:
      return "UNKNOWN";
  }
}

/**
 * Map an HTTP status to a catalog entry.
 *
 * Used for upstream provider failures (Gemini). AI-gateway errors already
 * carry explicit catalog codes, so this is the fallback for any other
 * fetch-based dependency.
 *
 * @param {number} status HTTP status code.
 * @returns {string} Catalog key.
 */
function catalogKeyForHttpStatus(status) {
  switch (status) {
    case 401:
    case 403:
      return "AI_NOT_CONFIGURED";
    case 404:
      return "AI_MODEL_GONE";
    case 413:
      return "AI_NOTES_TOO_LARGE";
    case 429:
      return "AI_RATE_LIMITED";
    case 500:
    case 502:
    case 503:
    case 504:
      return "AI_UNAVAILABLE";
    default:
      return "UNKNOWN";
  }
}

/**
 * Offline / DNS / TCP failures, detected by message because no runtime exposes
 * a usable `code` for them. Every browser and the Workers runtime words these
 * differently, so the list covers each vendor's phrasing:
 *   Chromium  "TypeError: Failed to fetch", "net::ERR_INTERNET_DISCONNECTED"
 *   Firefox   "NetworkError when attempting to fetch resource."
 *   Safari    "Load failed"
 *   Node/Undici "ECONNREFUSED", "fetch failed", "socket hang up"
 * `net::err` is kept as a prefix so new Chromium codes are covered too.
 */
const NETWORK_HINTS = [
  "failed to fetch",
  "fetch failed",
  "networkerror",
  "network request failed",
  "net::err",
  "load failed",
  "econnrefused",
  "enotfound",
  "econnreset",
  "etimedout",
  "esockettimedout",
  "socket hang up",
  "timeout",
];

/**
 * Normalize any thrown value into an `AppError`.
 *
 * Ordering matters and is deliberate:
 *   1. Already-normalized errors short-circuit, so a code that survived the
 *      Seroval boundary is preserved instead of being downgraded to UNKNOWN.
 *   2. Offline detection runs before provider/Postgres inspection because it is
 *      the most common failure and the message is the only signal available.
 *   3. The catalog is consulted before the Postgres switch, because a Supabase
 *      error may legitimately carry a catalog code set upstream.
 *
 * @param {unknown} error The thrown value.
 * @param {object} [options]
 * @param {string} [options.area] Operation identifier, e.g. "quiz.save".
 * @param {boolean} [options.exposeDetail] Set only on the CLIENT for errors that
 *   originated in this browser, where the raw text never leaves the machine.
 * @returns {AppError} Normalized error.
 */
function toAppError(error, { area = "", exposeDetail = false } = {}) {
  // 1. Already an AppError, here or rehydrated from the Seroval payload.
  if (isAppErrorLike(error)) {
    const code =
      typeof error.code === "string" && Object.hasOwn(ERROR_CATALOG, error.code)
        ? error.code
        : "UNKNOWN";
    const entry = catalogEntry(code);
    // `detail` arriving from the wire was already authorized server-side by
    // `toClientError`, so it is preserved rather than re-stripped here. The UI
    // still decides whether to render it.
    const inheritedDetail = typeof error.detail === "string" ? error.detail : "";
    const rebuilt = new AppError(
      code,
      error.userMessage || entry.userMessage,
      error.hint || entry.hint,
      typeof error.retryable === "boolean" ? error.retryable : entry.retryable,
      error.area || area,
      inheritedDetail,
    );
    // Preserve the original creation time so a rehydrated error is not shown
    // to a superadmin as if it had just happened.
    if (typeof error.occurredAt === "number") rebuilt.occurredAt = error.occurredAt;
    // Only an inherited `detail` counts as raw technical text here. Running
    // `rawTextOf` on an AppError would stage its friendly `message` as if it
    // were a secret, which would make every code-shaped error look like it
    // carried sensitive text and trigger a needless role-check RPC.
    return stageRawDetail(rebuilt, inheritedDetail);
  }

  // Anything thrown that is not an Error (strings, objects, null) still needs
  // to produce a usable message.
  if (!error || typeof error !== "object") {
    return fromCatalog("UNKNOWN", { area, raw: rawTextOf(error) });
  }

  // 2. Zod validation failures. The friendly message names the field so the
  //    user knows where to look, which is more useful than the generic text.
  if (error.name === "ZodError" && Array.isArray(error.issues) && error.issues.length > 0) {
    const field = String(error.issues[0]?.path?.[0] ?? "input");
    return fromCatalog("INVALID_INPUT", {
      area,
      raw: rawTextOf(error),
      message: `Invalid value for ${field}.`,
    });
  }

  // 3. Connectivity.
  const message = typeof error.message === "string" ? error.message.toLowerCase() : "";
  if (message && NETWORK_HINTS.some((hint) => message.includes(hint))) {
    return fromCatalog("NETWORK_UNAVAILABLE", { area, raw: rawTextOf(error) });
  }

  // 4. An explicit catalog code attached by a known module (e.g. Gemini).
  if (typeof error.code === "string" && Object.hasOwn(ERROR_CATALOG, error.code)) {
    return fromCatalog(error.code, { area, raw: rawTextOf(error) });
  }

  // 5. Supabase Auth. Checked before Postgres because both use string `code`
  //    values and the Postgres switch would claim these in its `default`.
  if (typeof error.code === "string") {
    const authKey = catalogKeyForAuthCode(error.code);
    if (authKey !== "UNKNOWN") {
      return fromCatalog(authKey, { area, raw: rawTextOf(error) });
    }
  }

  // 6. Supabase / Postgres.
  if (
    typeof error.code === "string" &&
    Object.hasOwn(ERROR_CATALOG, catalogKeyForPostgresCode(error.code))
  ) {
    const key = catalogKeyForPostgresCode(error.code);
    if (key !== "UNKNOWN") {
      return fromCatalog(key, { area, raw: rawTextOf(error) });
    }
    return fromCatalog("UNKNOWN", { area, raw: rawTextOf(error) });
  }

  // 7. Upstream HTTP provider.
  if (typeof error.status === "number" && error.status >= 400) {
    return fromCatalog(catalogKeyForHttpStatus(error.status), { area, raw: rawTextOf(error) });
  }

  // 8. Fallback.
  const fallback = fromCatalog("UNKNOWN", { area, raw: rawTextOf(error) });
  // `exposeDetail` is only ever true in the browser for locally-thrown errors,
  // where the raw text is the user's own and no server boundary was crossed.
  if (exposeDetail) fallback.detail = rawTextOf(error);
  return fallback;
}

/**
 * Final gate before an error is thrown from a server function.
 *
 * TanStack Start will serialize this error to the caller, so this is the last
 * point at which raw technical text can be withheld. If (and only if) the
 * caller is a verified superadmin, the staged raw cause is promoted onto
 * `detail` so the in-app panel can show it. Otherwise `detail` stays empty.
 *
 * The raw text is always written to the server log, because Sentry is
 * currently browser-only (see `sentry.config.js`) and the Worker log is the
 * only durable record of the underlying failure.
 *
 * @param {unknown} error Error about to be thrown.
 * @param {object} options
 * @param {string} [options.area] Operation identifier.
 * @param {object} [options.supabase] Authenticated Supabase client from the
 *   server-function context. Omitted for unauthenticated flows.
 * @param {string} [options.userId] Authenticated user id from the context.
 * @returns {Promise<AppError>} Error safe to throw to the client.
 */
async function toClientError(error, { area = "", supabase, userId } = {}) {
  const appError = toAppError(error, { area });
  const raw = rawDetailFor(appError);

  // No raw text means there is nothing to protect.
  if (!raw) return appError;

  // Never trust the payload to redact itself.
  appError.detail = "";

  // Fail closed: no verified identity, no technical detail. This is the branch
  // taken by the anonymous student flows in `student.functions.js`.
  if (!supabase || !userId) {
    console.error(`[${appError.code}] ${appError.area || area}`, error);
    return appError;
  }

  const { data, error: roleError } = await supabase.rpc("has_role", {
    _user_id: userId,
    _role: "superadmin",
  });

  if (roleError || data !== true) {
    console.error(`[${appError.code}] ${appError.area || area}`, error);
    return appError;
  }

  // Verified superadmin: the raw cause may cross to their browser.
  appError.detail = raw;
  return appError;
}

/**
 * Build the view model a component should render.
 *
 * This is a rendering gate, not a security gate: `detail` is only included for
 * superadmins, but the actual protection is that `detail` is empty for everyone
 * else (see the file header).
 *
 * @param {AppError} error Normalized error.
 * @param {object} [options]
 * @param {boolean} [options.isSuperadmin] Whether to include technical detail.
 * @param {string} [options.eventId] Sentry event id to quote as a reference.
 * @returns {{code: string, title: string, message: string, hint: string,
 *   retryable: boolean, area: string, timestamp: number, eventId?: string,
 *   detail?: string}} Render-ready view model.
 */
function describeError(error, { isSuperadmin = false, eventId } = {}) {
  const code = typeof error?.code === "string" ? error.code : "UNKNOWN";
  const entry = catalogEntry(code);

  const viewModel = {
    code,
    title: entry.title,
    message: error?.userMessage || entry.userMessage,
    hint: error?.hint || entry.hint,
    retryable: typeof error?.retryable === "boolean" ? error.retryable : entry.retryable,
    area: error?.area || "",
    timestamp: typeof error?.occurredAt === "number" ? error.occurredAt : Date.now(),
  };

  if (eventId) viewModel.eventId = eventId;

  const detail = isSuperadmin ? rawDetailFor(error) : "";
  if (detail) viewModel.detail = detail;

  return viewModel;
}

/**
 * Build an AppError directly from a catalog code, with no raw technical text.
 *
 * This is the constructor to use for DELIBERATE failures discovered by business
 * logic ("this PIN does not exist", "this quiz was already deleted"). Those are
 * not unexpected exceptions and must not be funnelled through `toAppError`:
 *
 *   toAppError(new Error("Quiz not found"), { area })
 *
 * works, but a plain Error carries no catalog code, so it lands on the UNKNOWN
 * fallback and the caller is shown "Something went wrong on our end" instead of
 * the accurate "We couldn't find what you were looking for". Naming the code
 * keeps the message truthful.
 *
 * No raw text is staged, so nothing sensitive can be attached by accident and
 * no role-check RPC is needed at the throw site.
 *
 * @param {string} code Catalog key.
 * @param {object} [options]
 * @param {string} [options.area] Operation identifier.
 * @param {string} [options.message] Override the friendly message.
 * @param {string} [options.hint] Override the actionable hint.
 * @param {boolean} [options.retryable] Override the retry affordance.
 * @returns {AppError} Normalized error ready to throw.
 */
function catalogError(code, { area = "", message = "", hint = "", retryable } = {}) {
  const entry = catalogEntry(code);
  return new AppError(
    Object.hasOwn(ERROR_CATALOG, code) ? code : "UNKNOWN",
    message || entry.userMessage,
    hint || entry.hint,
    typeof retryable === "boolean" ? retryable : entry.retryable,
    area,
    "",
  );
}

/**
 * Codes that represent a correct response to a bad or impossible request,
 * rather than a defect in the system.
 *
 * A student typing the wrong game code, or a teacher submitting an empty quiz
 * title, produces one of these. They must still be shown a friendly message and
 * retained for the superadmin panel, but they are not incidents: sending them
 * to Sentry at `error` level buries the genuine failures that actually need
 * someone to look. `isExpectedError` lets the reporter downgrade them to `info`
 * so they stay queryable without polluting the error-rate trend.
 */
const EXPECTED_CODES = new Set([
  "AUTH_INVALID_CREDENTIALS",
  "AUTH_EMAIL_UNCONFIRMED",
  "AUTH_ACCOUNT_DISABLED",
  "AUTH_RATE_LIMITED",
  "AUTH_INVALID_CODE",
  "AUTH_CODE_EXPIRED",
  "AUTH_TOO_MANY_ATTEMPTS",
  "FORBIDDEN",
  "NOT_FOUND",
  "INVALID_INPUT",
  "INVALID_PIN",
  "GAME_ENDED",
]);

/**
 * Whether a code describes an expected outcome rather than a defect.
 *
 * @param {string} code Catalog key.
 * @returns {boolean} True when the error is a normal response to bad input.
 */
function isExpectedError(code) {
  return EXPECTED_CODES.has(code);
}

export {
  AppError,
  ERROR_CATALOG,
  catalogError,
  catalogEntry,
  describeError,
  isAppErrorLike,
  isExpectedError,
  isRateLimitCode,
  rawDetailFor,
  stageRawDetail,
  toAppError,
  toClientError,
};
